/**
 * Rezdy API Service
 * Handles all interactions with the Rezdy API for tour availability
 */

// Using Firebase function proxy to avoid CORS and secure API key
// Staging URL (update to production URL when ready)
const REZDY_BASE_URL = 'https://us-central1-adrift-chatbot-staging.cloudfunctions.net/rezdyProxy';
const CACHE_TTL = 15 * 60 * 1000; // 15 minutes in milliseconds

// TypeScript interfaces for Rezdy data
export interface RezdyProduct {
  productCode: string;
  name: string;
  productType: string;
  productStatus?: string;
  description?: string;
  shortDescription?: string;
}

export interface RezdySession {
  startTime: string;
  startTimeLocal: string;
  endTime: string;
  endTimeLocal: string;
  seatsAvailable: number | null | undefined;
  totalPrice?: number;
  totalCurrency?: string;
  productCode: string;
  productName?: string;
}

export interface RezdyAvailabilityResponse {
  sessions: RezdySession[];
  requestStatus: {
    success: boolean;
    error?: any;
  };
}

export interface RezdyProductsResponse {
  products: RezdyProduct[];
  requestStatus: {
    success: boolean;
    error?: any;
  };
}

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

// Simple in-memory cache
class ApiCache {
  private cache: Map<string, CacheEntry<any>> = new Map();

  set<T>(key: string, data: T): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const age = Date.now() - entry.timestamp;
    if (age > CACHE_TTL) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  clear(): void {
    this.cache.clear();
  }

  getCacheAge(key: string): number | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    return Date.now() - entry.timestamp;
  }
}

const cache = new ApiCache();

/**
 * Make a request to the Rezdy API via Firebase function proxy
 */
async function makeRezdyRequest<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
  // Build query parameters with path as the first parameter
  const queryParams = new URLSearchParams({
    path: endpoint,
    ...params
  });

  const url = `${REZDY_BASE_URL}?${queryParams.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    mode: 'cors',
    headers: {
      'Accept': 'application/json'
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(`Rezdy API error: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetch all products from Rezdy
 * Results are cached for the session
 */
export async function fetchProducts(): Promise<RezdyProduct[]> {
  const cacheKey = 'products';
  const cached = cache.get<RezdyProduct[]>(cacheKey);
  
  if (cached) {
    console.log('Using cached products');
    return cached;
  }

  console.log('Fetching products from Rezdy API');
  const response = await makeRezdyRequest<RezdyProductsResponse>('/products');
  
  if (!response.requestStatus.success) {
    throw new Error('Failed to fetch products from Rezdy');
  }

  const products = response.products || [];
  cache.set(cacheKey, products);
  
  return products;
}

/**
 * Fetch availability for a specific date
 * Results are cached for 15 minutes
 */
export async function fetchAvailability(date: Date): Promise<RezdySession[]> {
  const dateStr = date.toISOString().split('T')[0];
  const cacheKey = `availability-${dateStr}`;
  const cached = cache.get<RezdySession[]>(cacheKey);
  
  if (cached) {
    console.log(`Using cached availability for ${dateStr}`);
    return cached;
  }

  console.log(`Fetching availability from Rezdy API for ${dateStr}`);

  // Get all products first
  const products = await fetchProducts();

  // Fetch availability for each product for the selected date
  const startTime = new Date(date);
  startTime.setHours(0, 0, 0, 0);
  
  const endTime = new Date(date);
  endTime.setHours(23, 59, 59, 999);

  const allSessions: RezdySession[] = [];

  // Fetch availability for all products in parallel
  const availabilityPromises = products.map(async (product) => {
    try {
      const params = {
        productCode: product.productCode,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString()
      };

      const response = await makeRezdyRequest<RezdyAvailabilityResponse>(
        '/availability',
        params
      );

      if (response.requestStatus.success && response.sessions) {
        // Add product name to each session
        return response.sessions.map(session => ({
          ...session,
          productCode: product.productCode,
          productName: product.name
        }));
      }
      return [];
    } catch (error) {
      console.warn(`Failed to fetch availability for ${product.productCode}:`, error);
      return [];
    }
  });

  const results = await Promise.all(availabilityPromises);
  results.forEach(sessions => allSessions.push(...sessions));

  // Cache the results
  cache.set(cacheKey, allSessions);

  return allSessions;
}

/**
 * Get cache age for a specific date
 */
export function getCacheAge(date: Date): number | null {
  const dateStr = date.toISOString().split('T')[0];
  const cacheKey = `availability-${dateStr}`;
  return cache.getCacheAge(cacheKey);
}

/**
 * Clear all cached data
 */
export function clearCache(): void {
  cache.clear();
  console.log('Cache cleared');
}

/**
 * Check if a session is actually available (has seats > 0)
 * Excludes null, undefined, negative, and zero values
 */
export function isSessionAvailable(session: RezdySession): boolean {
  const seatsAvailable = session.seatsAvailable;
  return seatsAvailable !== null && 
         seatsAvailable !== undefined && 
         seatsAvailable > 0;
}

/**
 * Find dates with availability near a given date
 * Useful for suggesting alternative dates when a date is fully booked
 */
export async function findNearbyAvailableDates(
  targetDate: Date,
  daysToCheck: number = 7
): Promise<Date[]> {
  const availableDates: Date[] = [];
  
  // Check dates after the target date
  for (let i = 1; i <= daysToCheck; i++) {
    const checkDate = new Date(targetDate);
    checkDate.setDate(targetDate.getDate() + i);
    
    const sessions = await fetchAvailability(checkDate);
    const hasAvailability = sessions.some(s => isSessionAvailable(s));
    
    if (hasAvailability) {
      availableDates.push(checkDate);
      if (availableDates.length >= 2) break; // Only need 2 suggestions
    }
  }

  return availableDates;
}

